export const eventEditMessage = code => ({
  EVENT_REVISION_CONFLICT: 'This event changed since you opened it. Reload the current details before saving.',
  ERROR_PERMISSIONS: 'Your account no longer has permission to edit this event.',
  EVENT_EDIT_NOT_RECEIVED: 'The request was not saved. Review your changes and save again.',
  INVALID_EVENT_UPLOAD: 'The selected upload has expired or is invalid. Remove it and select the file again before saving.',
  EVENT_NOT_FOUND: 'This event is no longer available.',
  INVALID_TRANSLATION_STATE: 'The translation is no longer waiting for this. Reload the current details to see its state.',
  INVALID_TRANSLATION_CONTENT: 'Write the translation in the same fields as the original.',
  INVALID_TRANSLATION_LANGUAGE: "The translation's languages changed since you opened it. Reload the current details to see its state.",
  'INVALID_EVENT_TEXT:competitionId': 'This competition is no longer available. Reload the current details.',
  INVALID_EVENT_VENUE_LOCKED: 'The event has started or has results, so its club and venue can no longer change. Reload the current details.',
  INVALID_EVENT_CATEGORY_LOCKED: 'Entries or draws now exist, so the category can no longer change. Reload the current details.',
  INVALID_EVENT_SPORT_LOCKED: 'A competition now exists, so the sport can no longer change. Reload the current details.',
  INVALID_EVENT_CLUB_UNAVAILABLE: 'The selected club is no longer active. Choose another club.',
  INVALID_EVENT_ORGANIZER: 'This organizer is no longer active or does not organize for the selected club. Choose another organizer.',
  INVALID_EVENT_IMAGES_CONFIRMATION: "The club's default images changed. Review the updated images again before saving.",
  INVALID_EVENT_ELAPSED_CONFIRMATION: 'A calendar boundary you changed is now in the past. Save again to keep the change.',
  SESSION_EXPIRED: 'Your session expired, so nothing was saved. Sign in again in a new tab, then save here; your changes are kept.',
}[code] || (code?.startsWith('INVALID_') ? 'Check the required fields and current options before saving.' : 'The save is not confirmed. Check its status before retrying.'))

// The refusals an author answers by reloading the current details: the event, its translation or its competition changed since the
// editor opened, or a history lock now holds a field.
export const reviewsCurrentDetails = code => ['EVENT_REVISION_CONFLICT', 'INVALID_TRANSLATION_STATE', 'INVALID_TRANSLATION_LANGUAGE',
  'INVALID_EVENT_TEXT:competitionId', 'INVALID_EVENT_VENUE_LOCKED', 'INVALID_EVENT_CATEGORY_LOCKED', 'INVALID_EVENT_SPORT_LOCKED'].includes(code)

// The editor field a refusal is about, or null: the refusal reads at that field, where the author fixes it.
export const eventEditField = code => ({ INVALID_EVENT_CLUB_UNAVAILABLE: 'club', INVALID_EVENT_ORGANIZER: 'organizer',
  INVALID_EVENT_IMAGES_CONFIRMATION: 'images' })[code] || null

// `retrying` marks a send that asks again for an operation sent before (Recover, Check). The gateway answers 401 before it publishes
// anything, so a first send it refuses for an expired session never reached Events and ends at once; a refused recovery leaves the first
// send's outcome open.
export function runEventEdit({ id, operationId, correlationId, submit, read, socket, retrying = false, timeoutMs = 60000, pollMs = 2000 }) {
  let finished = false, resolve, timer
  const promise = new Promise(done => { resolve = done })
  const finish = result => {
    if (finished) return
    finished = true
    clearTimeout(timer); clearTimeout(deadline)
    socket?.off('events-response', receive)
    resolve(result)
  }
  const accept = data => {
    if (data?.id && data.id !== id) return
    if (data?.status === 'saved' && data.id === id) finish(data)
    // OPERATION_PAYLOAD_CONFLICT refuses a copy of this operation ID that carries another payload; the operation keeps its own outcome,
    // which the socket or the read gives later. So the conflict never ends the edit, and no text is needed for it.
    if (data?.status === 'error' && data.message !== 'OPERATION_PAYLOAD_CONFLICT') finish({ ...data, code: data.message, message: eventEditMessage(data.message) })
  }
  // The gateway forwards the correlationId on events-response only for a message that names its event (sportya-api
  // ApiGateway/consumers.js:258), and event-edited does not, so the answer usually arrives without one. The operation ID is unique per
  // edit, and every answer to it reports the same durable receipt; a correlationId, when present, still has to be this request's.
  const receive = message => {
    if (message?.operation !== 'event-edited' || message.operationId !== operationId || (message.correlationId != null && message.correlationId !== correlationId)) return
    if (message.success === true) accept(message.data)
    else if (message.success === false && message.retryable === false) accept({ ...message.data, status: 'error', message: message.message })
  }
  const poll = async () => {
    if (finished) return
    try { accept((await read(operationId)).data) } catch { /* An unavailable query does not establish that saving failed. */ }
    if (!finished) timer = setTimeout(poll, pollMs)
  }
  const deadline = setTimeout(() => finish({ status: 'unknown' }), timeoutMs)
  socket?.on('events-response', receive)
  timer = setTimeout(poll, submit ? pollMs : 0)
  if (submit) Promise.resolve().then(() => !finished && submit()).catch(error => {
    const status = error.response?.status
    if (status === 401) finish(retrying ? { status: 'unknown', code: 'SESSION_EXPIRED' } : { status: 'error', code: 'SESSION_EXPIRED', message: eventEditMessage('SESSION_EXPIRED') })
    else if (status >= 400 && status < 500 && ![403, 408, 429].includes(status)) accept({ status: 'error', message: error.response.data?.message })
  })
  return { promise, cancel: () => finish({ status: 'cancelled' }) }
}
